import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { SummaryPhase, SummaryRow } from '@/types';
import { formatResumePrice } from '@/utils/price.utils';

/** Pillola posizione "P-n" (port del PlateContainer legacy). */
const PositionChip = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 26,
  padding: '1px 5px',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.4,
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: 4,
}));

interface SummarySheetProps {
  phase: SummaryPhase;
}

const SheetRoot = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: '#F9F3EE',
  '&:first-of-type': { marginTop: 0 },
}));

const SheetHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2, 0, 0, 1.75),
}));

const Circle = styled('span')(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  border: `2px solid ${theme.palette.secondary.main}`,
  flexShrink: 0,
}));

/** Nome riga: "step: substep" (o solo substep se coincidono), come il legacy `stepSubstepName`. */
function stepSubstepName(stepName: string, substepName: string): string {
  return stepName === substepName ? substepName : `${stepName}: ${substepName}`;
}

/** Somma dei priceDiff (extra vs capitolato) delle righe di un gruppo. */
function subtotal(rows: SummaryRow[]): number {
  return rows.reduce((sum, r) => sum + (r.priceDiff ?? 0), 0);
}

function RowsTable({ title, rows }: { title: string | null; rows: SummaryRow[] }) {
  return (
    <Table size="small" sx={{ mb: 2.5, '&:last-child': { mb: 0 } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase' }}>{title ?? ''}</TableCell>
          <TableCell />
          <TableCell align="right" sx={{ fontWeight: 600 }}>
            Valore
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            <TableCell sx={{ textTransform: 'uppercase', fontSize: 13, fontWeight: 500, verticalAlign: 'top' }}>
              {stepSubstepName(row.stepName, row.substepName)}
            </TableCell>
            <TableCell sx={{ textTransform: 'uppercase', fontSize: 13, verticalAlign: 'top' }}>
              <Box>{row.choiceLabel}</Box>
              {row.positions && row.positions.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {[...row.positions]
                    .sort((a, b) => a - b)
                    .map((p) => (
                      <PositionChip key={p}>P-{p}</PositionChip>
                    ))}
                </Stack>
              )}
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontSize: 13, whiteSpace: 'nowrap', color: row.priceDiff ? 'primary.main' : 'text.primary' }}
            >
              {formatResumePrice(row.priceDiff)}
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell />
          <TableCell />
          <TableCell align="right" sx={{ fontWeight: 750, fontSize: 20, color: 'primary.main', whiteSpace: 'nowrap' }}>
            {formatResumePrice(subtotal(rows))}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

/**
 * Foglio di riepilogo di una fase (port di SummarySheet legacy). Le righe con `roomLabel` vengono
 * raggruppate per stanza (fase BAGNO), altrimenti un'unica tabella. Fase senza scelte → stato
 * "Non hai ancora effettuato le tue scelte".
 */
export function SummarySheet({ phase }: SummarySheetProps) {
  // Raggruppa le righe per stanza (roomLabel); null → gruppo unico senza titolo.
  const groups = new Map<string | null, SummaryRow[]>();
  for (const row of phase.rows) {
    const key = row.roomLabel;
    const list = groups.get(key) ?? [];
    list.push(row);
    groups.set(key, list);
  }
  const isEmpty = phase.rows.length === 0;

  return (
    <SheetRoot sx={{ backgroundColor: isEmpty ? '#f6f6f6' : '#F9F3EE' }}>
      <SheetHeader sx={{ pb: isEmpty ? 2 : 0 }}>
        <Circle sx={{ borderColor: isEmpty ? '#878787' : 'secondary.main' }} />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontWeight: 600, fontSize: 18, textTransform: 'uppercase', color: isEmpty ? '#878787' : 'text.primary' }}>
            {phase.phaseName}
          </Typography>
          {isEmpty && (
            <Typography sx={{ fontFamily: '"miller", serif', fontStyle: 'italic', fontSize: 13, color: '#878787' }}>
              Non hai ancora effettuato le tue scelte
            </Typography>
          )}
        </Box>
      </SheetHeader>

      {!isEmpty &&
        [...groups.entries()].map(([roomLabel, rows], i) => (
          <RowsTable key={i} title={roomLabel} rows={rows} />
        ))}
    </SheetRoot>
  );
}
